import { useState, useEffect, useRef, useCallback } from "react";
import { communicationApi, mapCommunicationError } from "../api/communicationApi.js";
import { communicationSocket } from "../socket/communicationSocketClient.js";

export function generateIdempotencyKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "idem_" + Math.random().toString(36).substring(2, 15) + "_" + Date.now();
}

/**
 * Core state store logic separate from React for testability and headless usage
 */
export class CommunicationMessageStore {
  constructor({
    apiClient = communicationApi,
    socketClient = communicationSocket,
    onStateChange = () => {}
  } = {}) {
    this.apiClient = apiClient;
    this.socketClient = socketClient;
    this.onStateChange = onStateChange;

    this.conversationId = null;
    this.messages = [];
    this.nextCursor = null;
    this.loading = false;
    this.loadingOlder = false;
    this.sending = false;
    this.error = null;
    this.isConnected = socketClient.connected;

    this.unsubscribers = [];
    this.setupSocketListeners();
  }

  setupSocketListeners() {
    this.unsubscribers.push(
      this.socketClient.onConnectionChange(({ isConnected, error }) => {
        this.isConnected = isConnected;
        if (error) this.error = error;
        this.emitChange();
      })
    );

    this.unsubscribers.push(
      this.socketClient.onMessageNew(incoming => {
        if (!incoming || incoming.conversationId !== this.conversationId) return;
        this.addOrUpdateMessage(incoming);
      })
    );

    this.unsubscribers.push(
      this.socketClient.onMessageUpdated(updated => {
        if (!updated || updated.conversationId !== this.conversationId) return;
        this.updateMessageContent(updated._id, updated.text, updated.editedAt);
      })
    );

    this.unsubscribers.push(
      this.socketClient.onMessageDeleted(deleted => {
        if (!deleted || deleted.conversationId !== this.conversationId) return;
        this.markMessageDeleted(deleted._id, deleted.deletedAt);
      })
    );
  }

  destroy() {
    this.unsubscribers.forEach(unsub => unsub());
    this.unsubscribers = [];
  }

  emitChange() {
    this.onStateChange(this.getState());
  }

  getState() {
    return {
      conversationId: this.conversationId,
      messages: [...this.messages],
      nextCursor: this.nextCursor,
      hasMore: Boolean(this.nextCursor),
      loading: this.loading,
      loadingOlder: this.loadingOlder,
      sending: this.sending,
      error: this.error,
      isConnected: this.isConnected
    };
  }

  async selectConversation(conversationId) {
    if (!conversationId) return;
    if (this.conversationId === conversationId && this.messages.length > 0) {
      return;
    }

    // Leave prior room if different
    if (this.conversationId && this.conversationId !== conversationId) {
      this.socketClient.leaveConversation(this.conversationId).catch(() => {});
    }

    this.conversationId = conversationId;
    this.messages = [];
    this.nextCursor = null;
    this.loading = true;
    this.error = null;
    this.emitChange();

    try {
      // 1. Join Socket room
      await this.socketClient.joinConversation(conversationId);

      // 2. Fetch initial messages via REST cursor API
      const result = await this.apiClient.listMessages(conversationId, { limit: 30 });
      // REST returns messages ordered sentAt DESC, so reverse for chat view (oldest to newest)
      const fetched = Array.isArray(result.messages) ? [...result.messages].reverse() : [];

      this.messages = fetched;
      this.nextCursor = result.nextCursor || null;
      this.loading = false;
      this.emitChange();

      // 3. Mark newest message read
      if (fetched.length > 0) {
        const newest = fetched[fetched.length - 1];
        this.socketClient.markRead({ conversationId, messageId: newest._id }).catch(() => {});
      }
    } catch (err) {
      this.loading = false;
      this.error = mapCommunicationError(err);
      this.emitChange();
    }
  }

  async loadOlderMessages() {
    if (!this.conversationId || !this.nextCursor || this.loadingOlder) {
      return;
    }

    this.loadingOlder = true;
    this.error = null;
    this.emitChange();

    try {
      const result = await this.apiClient.listMessages(this.conversationId, {
        beforeSentAt: this.nextCursor.beforeSentAt,
        beforeId: this.nextCursor.beforeId,
        limit: 30
      });

      const olderMessages = Array.isArray(result.messages) ? [...result.messages].reverse() : [];

      // Deduplicate and prepend
      const existingIds = new Set(this.messages.map(m => m._id));
      const filteredOlder = olderMessages.filter(m => !existingIds.has(m._id));

      this.messages = [...filteredOlder, ...this.messages];
      this.nextCursor = result.nextCursor || null;
      this.loadingOlder = false;
      this.emitChange();
    } catch (err) {
      this.loadingOlder = false;
      this.error = mapCommunicationError(err);
      this.emitChange();
    }
  }

  async sendMessage(text, retryIdempotencyKey = null) {
    if (!this.conversationId || !text || !text.trim()) return;

    this.sending = true;
    this.error = null;
    this.emitChange();

    const idempotencyKey = retryIdempotencyKey || generateIdempotencyKey();

    try {
      let savedMessage;
      if (this.socketClient.connected) {
        savedMessage = await this.socketClient.sendMessage({
          conversationId: this.conversationId,
          idempotencyKey,
          text
        });
      } else {
        const res = await this.apiClient.sendMessage(this.conversationId, {
          idempotencyKey,
          text
        });
        savedMessage = res.message;
      }

      this.addOrUpdateMessage(savedMessage);
      this.sending = false;
      this.emitChange();
      return savedMessage;
    } catch (err) {
      this.sending = false;
      this.error = mapCommunicationError(err);
      this.emitChange();
      throw err;
    }
  }

  async editMessage(messageId, text) {
    if (!this.conversationId || !messageId || !text || !text.trim()) return;

    this.error = null;
    this.emitChange();

    try {
      let updatedMessage;
      if (this.socketClient.connected) {
        updatedMessage = await this.socketClient.editMessage({
          conversationId: this.conversationId,
          messageId,
          text
        });
      } else {
        const res = await this.apiClient.editMessage(this.conversationId, messageId, text);
        updatedMessage = res.message;
      }

      this.updateMessageContent(messageId, text, updatedMessage.editedAt || new Date());
      return updatedMessage;
    } catch (err) {
      this.error = mapCommunicationError(err);
      this.emitChange();
      throw err;
    }
  }

  async deleteMessage(messageId) {
    if (!this.conversationId || !messageId) return;

    this.error = null;
    this.emitChange();

    try {
      let deletedStub;
      if (this.socketClient.connected) {
        deletedStub = await this.socketClient.deleteMessage({
          conversationId: this.conversationId,
          messageId
        });
      } else {
        const res = await this.apiClient.deleteMessage(this.conversationId, messageId);
        deletedStub = res.message;
      }

      this.markMessageDeleted(messageId, deletedStub.deletedAt || new Date());
      return deletedStub;
    } catch (err) {
      this.error = mapCommunicationError(err);
      this.emitChange();
      throw err;
    }
  }

  // --- Deduplication & State Mutations ---
  addOrUpdateMessage(message) {
    if (!message || !message._id) return;
    const index = this.messages.findIndex(m => m._id === message._id);
    if (index !== -1) {
      // Update existing message
      this.messages[index] = { ...this.messages[index], ...message };
    } else {
      // Append new message (prevents duplicate insertion)
      this.messages.push(message);
    }
    this.emitChange();
  }

  updateMessageContent(messageId, text, editedAt) {
    const index = this.messages.findIndex(m => m._id === messageId);
    if (index !== -1) {
      this.messages[index] = {
        ...this.messages[index],
        text,
        editedAt: editedAt || new Date()
      };
      this.emitChange();
    }
  }

  markMessageDeleted(messageId, deletedAt) {
    const index = this.messages.findIndex(m => m._id === messageId);
    if (index !== -1) {
      this.messages[index] = {
        ...this.messages[index],
        status: "SOFT_DELETED",
        deletedAt: deletedAt || new Date()
      };
      this.emitChange();
    }
  }
}

/**
 * React hook consuming CommunicationMessageStore
 */
export function useCommunication({ conversationId = null, apiClient, socketClient } = {}) {
  const storeRef = useRef(null);
  const [state, setState] = useState(() => {
    storeRef.current = new CommunicationMessageStore({
      apiClient,
      socketClient,
      onStateChange: updated => setState(updated)
    });
    return storeRef.current.getState();
  });

  useEffect(() => {
    const store = storeRef.current;
    if (conversationId) {
      store.selectConversation(conversationId);
    }
  }, [conversationId]);

  useEffect(() => {
    return () => {
      if (storeRef.current) {
        storeRef.current.destroy();
      }
    };
  }, []);

  const selectConversation = useCallback(id => storeRef.current?.selectConversation(id), []);
  const loadOlderMessages = useCallback(() => storeRef.current?.loadOlderMessages(), []);
  const sendMessage = useCallback((text, key) => storeRef.current?.sendMessage(text, key), []);
  const editMessage = useCallback((id, text) => storeRef.current?.editMessage(id, text), []);
  const deleteMessage = useCallback(id => storeRef.current?.deleteMessage(id), []);

  return {
    ...state,
    selectConversation,
    loadOlderMessages,
    sendMessage,
    editMessage,
    deleteMessage
  };
}
